import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef, type SortingState } from "@tanstack/react-table";
import { toast } from "sonner";

import {
  fetchOrganizations,
  fetchOrganization,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  addUserToOrganization,
  removeUserFromOrganization,
  inviteUserByPhone,
  addUnitToOrganization,
  removeUnitFromOrganization,
  downloadOrganizationSessionsReport,
  fetchUsers,
  fetchUnits,
  Organization,
  OrganizationDetail,
  OrganizationUnitSummary,
  UserRecord,
} from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { SlidePanel } from "@/components/SlidePanel";
import { OrgRoleBadge } from "@/components/OrgRoleBadge";
import { PendingInvitations } from "@/components/PendingInvitations";
import { inviteSmsReasonText } from "@/lib/phone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { DollarSign, Download, Search, Wifi, WifiOff, X, Zap } from "@/components/icons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { statusBadgeProps } from "@/lib/unit-helpers";
import { useAuth } from "@/app/AuthContext";
import { OrgSessionsReport } from "@/views/OrgSessionsReport";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

// Map a unit's status (online + state_name) to an icon + tailwind color +
// human label for the row in the org units list. Uses the same variant
// mapping as `statusBadgeProps` so colors stay in sync with the rest of the app.
//
// IMPORTANT: this project does NOT define text-success/text-warning/text-info
// tokens. The Badge component maps variants to real Tailwind colors internally
// (see components/ui/badge.tsx). Mirror that mapping here for icons.
function statusIconProps(
  isOnline: boolean | undefined,
  stateName: string | null | undefined,
): { Icon: React.ComponentType<{ className?: string }>; color: string; label: string } {
  if (isOnline === undefined) {
    return { Icon: Wifi, color: "text-muted-foreground/40", label: "Status unknown — unit has not connected" };
  }
  if (!isOnline) {
    return { Icon: WifiOff, color: "text-muted-foreground", label: "Offline" };
  }
  const s = (stateName || "").toLowerCase();
  // Charging gets its own icon — most useful at-a-glance signal.
  if (s === "charging" || s === "charging_simple") {
    return { Icon: Zap, color: "text-emerald-500", label: "Charging" };
  }
  const { variant } = statusBadgeProps(s, true);
  const colorByVariant: Record<typeof variant, string> = {
    success: "text-emerald-500",
    error: "text-red-600 dark:text-red-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-sky-600 dark:text-sky-400",
    primary: "text-primary",
    neutral: "text-muted-foreground",
  };
  return {
    Icon: Wifi,
    color: colorByVariant[variant],
    label: stateName ? stateName.charAt(0).toUpperCase() + stateName.slice(1) : "Online",
  };
}

function paidBadge(paidUntil: string | null) {
  if (!paidUntil) return <span className="opacity-40">—</span>;
  const d = new Date(paidUntil + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const label = d.toLocaleDateString();
  return d >= now ? (
    <Badge variant="success">{label}</Badge>
  ) : (
    <Badge variant="error">{label}</Badge>
  );
}

/* ------------------------------------------------------------------ */
/*  Create / Edit Modal                                                */
/* ------------------------------------------------------------------ */

interface OrgFormData {
  name: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  paid_until: string;
  comment: string;
}

const emptyForm: OrgFormData = {
  name: "",
  contact_person: "",
  contact_phone: "",
  contact_email: "",
  paid_until: "",
  comment: "",
};

function OrgFormModal({
  open,
  onOpenChange,
  title,
  initial,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  initial: OrgFormData;
  onSubmit: (data: OrgFormData) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState<OrgFormData>(initial);

  React.useEffect(() => {
    if (open) setForm(initial);
  }, [open]);

  const set = (k: keyof OrgFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form);
          }}
        >
          <div>
            <Label>Organization Name *</Label>
            <Input className="mt-1.5" value={form.name} onChange={set("name")} required minLength={2} maxLength={100} placeholder="e.g. Acme Corp" />
          </div>
          <div>
            <Label>Contact Person</Label>
            <Input className="mt-1.5" value={form.contact_person} onChange={set("contact_person")} maxLength={200} placeholder="Full name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <Input className="mt-1.5" value={form.contact_phone} onChange={set("contact_phone")} maxLength={50} placeholder="+1 555-123-4567" />
            </div>
            <div>
              <Label>Email</Label>
              <Input className="mt-1.5" type="email" value={form.contact_email} onChange={set("contact_email")} maxLength={200} placeholder="contact@example.com" />
            </div>
          </div>
          <div>
            <Label>Paid Until</Label>
            <Input className="mt-1.5" type="date" value={form.paid_until} onChange={set("paid_until")} />
          </div>
          <div>
            <Label>Comment</Label>
            <textarea
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              rows={3}
              maxLength={2000}
              value={form.comment}
              onChange={set("comment")}
              placeholder="Notes about this organization..."
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isPending || !form.name.trim()}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Add User Modal                                                     */
/* ------------------------------------------------------------------ */

function AddUserModal({
  open,
  onOpenChange,
  orgId,
  existingUserIds,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: number;
  existingUserIds: Set<number>;
  onAdded: () => void;
}) {
  // Only a system admin may add an EXISTING account as a manager (the user-search
  // list below) or grant the 'manager' role. An org manager reaching this modal can
  // only invite DRIVERS by phone — the backend enforces both rules; the UI mirrors
  // them so a manager never sees controls that would 403.
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState("");
  // Debounce so we don't fire on every keystroke.
  const [searchDebounced, setSearchDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const usersQ = useQuery({
    queryKey: ["users", "addToOrg", searchDebounced],
    queryFn: () => fetchUsers({ q: searchDebounced || undefined, limit: 50 }),
    // The user directory (fetchUsers) is an admin-only endpoint; never query it for
    // a plain org manager (it would 403 and the list is hidden for them anyway).
    enabled: open && isAdmin,
  });

  const addM = useMutation({
    // This dialog adds organization MANAGERS. Drivers are created via the driver
    // import/membership path, not here. Role is passed explicitly (no default).
    mutationFn: (userId: number) => addUserToOrganization(orgId, userId, "manager"),
    onSuccess: () => {
      toast.success("User added");
      onAdded();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed to add user"),
  });

  // Invite-by-phone: add someone who may not have an account yet. Existing users
  // are added immediately; unknown phones get a pending invitation that is
  // consumed when they register with that number.
  const [phone, setPhone] = useState("");
  const [inviteRole, setInviteRole] = useState<"manager" | "driver">("driver");
  const inviteQueryClient = useQueryClient();
  const inviteM = useMutation({
    mutationFn: () => inviteUserByPhone(orgId, phone.trim(), inviteRole),
    onSuccess: (res) => {
      if (res.status === "added") {
        const names = (res.added ?? []).map((a) => a.username).join(", ");
        toast.success(names ? `Added existing user: ${names}` : "User added");
        onAdded();
        onOpenChange(false);
        return;
      }
      // Invites are idempotent per org+phone: a repeat re-uses the existing row
      // (and re-sends the text) rather than creating a second invitation.
      const verb = res.already_pending ? "re-sent" : "sent";
      if (res.sms_sent) {
        toast.success(`Invitation ${verb} by text message`);
      } else {
        // The invitation IS recorded either way — say why the text didn't go out
        // rather than implying nothing happened.
        toast.warning(
          `Invitation recorded, but not texted. ${inviteSmsReasonText(res.sms_reason)}`,
        );
      }
      setPhone("");
      inviteQueryClient.invalidateQueries({ queryKey: ["org-invitations", orgId] });
      onAdded();
    },
    // The backend returns stable error codes (invalid_phone / unsupported_country
    // / role_conflict); its message is already operator-facing, so show it as-is.
    onError: (e: any) => toast.error(e?.message || "Failed to invite"),
  });

  const available = useMemo(() => {
    const users = usersQ.data?.users ?? [];
    return users.filter((u) => !existingUserIds.has(u.id));
  }, [usersQ.data, existingUserIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add User to Organization</DialogTitle>
        </DialogHeader>
        {/* Search + add an EXISTING account as a manager — system-admin only. */}
        {isAdmin && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
              <Input
                className="pl-9"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {usersQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">Loading...</div>}
              {available.length === 0 && !usersQ.isLoading && (
                <div className="text-sm opacity-50 py-4 text-center">No matching users</div>
              )}
              {available.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{u.username}</span>
                    <Badge variant={u.role === "admin" ? "primary" : "neutral"} className="badge-sm">
                      {u.role}
                    </Badge>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => addM.mutate(u.id)} disabled={addM.isPending}>
                    Add
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Invite by phone — for people who don't have an account yet. */}
        <div className={`${isAdmin ? "border-t pt-3 mt-1 " : ""}space-y-2`}>
          <div className="text-sm font-medium">Invite by phone</div>
          <div className="text-xs opacity-60">
            If they already have an account they're added now; otherwise they'll
            join when they register with this phone number.
          </div>
          <div className="flex items-center gap-2">
            <Input
              className="flex-1"
              type="tel"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {/* Only a system admin may grant the 'manager' role. A plain org manager
                invites drivers only (server-enforced), so they see a fixed label. */}
            {isAdmin ? (
              <select
                className="h-9 rounded-4xl border border-input bg-input/30 px-3 text-sm"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as "manager" | "driver")}
              >
                <option value="driver">Driver</option>
                <option value="manager">Manager</option>
              </select>
            ) : (
              <span className="h-9 inline-flex items-center rounded-4xl border border-input bg-input/30 px-3 text-sm opacity-70">
                Driver
              </span>
            )}
            <Button
              size="sm"
              onClick={() => inviteM.mutate()}
              disabled={inviteM.isPending || phone.trim().length < 5}
            >
              {inviteM.isPending ? "…" : "Invite"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Add Unit Modal                                                     */
/* ------------------------------------------------------------------ */

function AddUnitModal({
  open,
  onOpenChange,
  orgId,
  existingUnitIds,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: number;
  existingUnitIds: Set<string>;
  onAdded: () => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);
  const unitsQ = useQuery({
    queryKey: ["units-search", debouncedSearch],
    queryFn: () => fetchUnits({ start: 0, end: 50, search: debouncedSearch || undefined }),
    enabled: open,
  });

  const addM = useMutation({
    mutationFn: (unitId: string) => addUnitToOrganization(orgId, unitId),
    onSuccess: () => {
      toast.success("Unit added");
      onAdded();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed to add unit"),
  });

  const available = useMemo(() => {
    const units = unitsQ.data?.units ?? [];
    return units.filter((u) => !existingUnitIds.has(u.unit_id));
  }, [unitsQ.data, existingUnitIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Unit to Organization</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input
            className="pl-9"
            placeholder="Search by ID or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {unitsQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">Loading...</div>}
          {available.length === 0 && !unitsQ.isLoading && (
            <div className="text-sm opacity-50 py-4 text-center">No matching units</div>
          )}
          {available.slice(0, 50).map((u) => (
            <div key={u.unit_id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm font-mono">{u.unit_id}</span>
                {u.name && <span className="text-xs opacity-50">{u.name}</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(u.unit_id)} disabled={addM.isPending}>
                Add
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Export Sessions CSV (admin only)                                   */
/* ------------------------------------------------------------------ */

function ExportSessionsSection({ orgId }: { orgId: number }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (start && end && end < start) {
      toast.error("End date must be on or after start date");
      return;
    }
    setDownloading(true);
    try {
      const { blob, filename } = await downloadOrganizationSessionsReport(orgId, {
        start: start || undefined,
        end: end || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Failed to generate report");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pt-4 border-t border-border">
      <h4 className="font-semibold text-sm mb-1">Reports</h4>
      <p className="text-xs opacity-60 mb-2">
        Export charging sessions for all of this organization's chargers as CSV.
        Leave dates empty for the last 31 days (max range 366 days).
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">From</Label>
          <Input
            className="mt-1 h-8 w-[150px]"
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input
            className="mt-1 h-8 w-[150px]"
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        <Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading}>
          <Download className="h-3.5 w-3.5 mr-1.5" />
          {downloading ? "Generating..." : "Export Sessions CSV"}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Organization Detail Panel                                          */
/* ------------------------------------------------------------------ */

export function OrgDetailPanel({
  orgId,
  onDeleted,
  mode = "panel",
}: {
  orgId: number;
  onDeleted: () => void;
  /** "panel" = brief slide-over (shows a link to the full page);
   *  "page"  = full details page (shows the Reports area). */
  mode?: "panel" | "page";
}) {
  const queryClient = useQueryClient();
  const { isAdmin, managerOrgIds } = useAuth();
  // A manager of THIS org may invite people to it (drivers only — see AddUserModal);
  // a system admin may manage any org. managerOrgIds is empty for system admins.
  const canManageOrg = isAdmin || managerOrgIds.includes(orgId);
  const [editOpen, setEditOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUnitOpen, setAddUnitOpen] = useState(false);

  const detailQ = useQuery({
    queryKey: ["organization", orgId],
    queryFn: () => fetchOrganization(orgId),
    refetchInterval: 15_000,
  });

  const org = detailQ.data?.organization;

  const editM = useMutation({
    mutationFn: (data: OrgFormData) => updateOrganization(orgId, data),
    onSuccess: () => {
      toast.success("Organization updated");
      setEditOpen(false);
      detailQ.refetch();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (e: any) => toast.error(e?.message || "Update failed"),
  });

  const deleteM = useMutation({
    mutationFn: () => deleteOrganization(orgId),
    onSuccess: () => {
      toast.success("Organization deleted");
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      onDeleted();
    },
    onError: (e: any) => toast.error(e?.message || "Delete failed"),
  });

  const removeUserM = useMutation({
    mutationFn: (userId: number) => removeUserFromOrganization(orgId, userId),
    onSuccess: () => {
      toast.success("User removed");
      detailQ.refetch();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (e: any) => toast.error(e?.message || "Remove failed"),
  });

  const removeUnitM = useMutation({
    mutationFn: (unitId: string) => removeUnitFromOrganization(orgId, unitId),
    onSuccess: () => {
      toast.success("Unit removed");
      detailQ.refetch();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (e: any) => toast.error(e?.message || "Remove failed"),
  });

  if (!org) {
    return <div className="text-sm opacity-50">Loading...</div>;
  }

  const refreshDetail = () => {
    detailQ.refetch();
    queryClient.invalidateQueries({ queryKey: ["organizations"] });
  };

  return (
    <div className="space-y-5">
      {/* Meta */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">{org.name}</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>Edit</Button>
            {isAdmin && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (confirm(`Delete "${org.name}"?\n\nAll users will revert to the free tier and their access to organization units will be revoked.`))
                    deleteM.mutate();
                }}
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {mode === "panel" && (
          <Link
            to={`/organizations/${orgId}`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Open full details →
          </Link>
        )}

        <div className="text-xs opacity-60">
          Created {new Date(org.created_at + "Z").toLocaleString()}
        </div>

        {org.contact_person && (
          <div><span className="font-medium">Contact:</span> {org.contact_person}</div>
        )}
        {org.contact_phone && (
          <div><span className="font-medium">Phone:</span> {org.contact_phone}</div>
        )}
        {org.contact_email && (
          <div><span className="font-medium">Email:</span> {org.contact_email}</div>
        )}
        {org.paid_until && (
          <div className="flex items-center gap-2">
            <span className="font-medium">Paid until:</span> {paidBadge(org.paid_until)}
          </div>
        )}
        {org.comment && (
          <div><span className="font-medium">Comment:</span> {org.comment}</div>
        )}

        {org.account_custom_id && (
          <div className="mt-3 pt-3 border-t border-border space-y-1.5">
            <div>
              <span className="font-medium">Account ID:</span>{" "}
              <span className="font-mono text-xs">{org.account_custom_id}</span>
            </div>
          </div>
        )}
      </div>

      {/* Users */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm">Users ({org.users.length})</h4>
          {canManageOrg && (
            <Button size="sm" variant="outline" onClick={() => setAddUserOpen(true)}>+ Add User</Button>
          )}
        </div>
        {org.users.length === 0 ? (
          <div className="text-sm opacity-50 italic">No users</div>
        ) : (
          <div className="space-y-1">
            {org.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 text-sm">
                <div className="flex items-center gap-2">
                  {/* Admins can open the full User Details page; the route is
                      admin-only, so org managers keep the plain name. */}
                  {isAdmin ? (
                    <Link
                      to={`/users/${u.id}`}
                      className="font-medium hover:underline"
                    >
                      {u.username}
                    </Link>
                  ) : (
                    <span className="font-medium">{u.username}</span>
                  )}
                  <OrgRoleBadge role={u.org_role} showNone />
                  {u.role === "admin" && (
                    <Badge variant="warning" className="badge-sm">admin</Badge>
                  )}
                  {isAdmin && (
                    <Badge variant={u.tier === "commercial" ? "success" : "secondary"} className="badge-sm">{u.tier}</Badge>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-destructive hover:text-destructive"
                    onClick={() => {
                      if (confirm(`Remove "${u.username}"? Tier reverts to free and access to organization units will be revoked.`))
                        removeUserM.mutate(u.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending phone invitations — people who have been invited but have not
          registered yet. Recorded even when the SMS channel is off, so without
          this section they were invisible. */}
      <PendingInvitations orgId={orgId} canManage={canManageOrg} isAdmin={isAdmin} />

      {/* Units */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-baseline gap-2">
            <h4 className="font-semibold text-sm">Units ({org.unit_ids.length})</h4>
            {typeof org.total_capacity_kw === "number" && org.unit_ids.length > 0 && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground cursor-default">
                      · {org.total_capacity_kw.toLocaleString()} kW capacity
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Deliverable capacity: {org.total_capacity_amps?.toLocaleString()} A
                    {typeof org.nominal_line_voltage === "number"
                      ? ` × ${org.nominal_line_voltage} V`
                      : ""}
                    {" "}— sum of each charger's max current, capped by load-balancing group limits
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setAddUnitOpen(true)}>+ Add Unit</Button>
          )}
        </div>
        {org.unit_ids.length === 0 ? (
          <div className="text-sm opacity-50 italic">No units</div>
        ) : (
          <TooltipProvider delayDuration={150}>
          <div className="space-y-1">
            {((org.units && org.units.length > 0
              ? org.units
              : org.unit_ids.map((uid) => ({ unit_id: uid }))) as OrganizationUnitSummary[]
            ).map((u) => {
              const isPaid =
                u.work_mode === "commercial-public" && u.pricing_mode === "paid";
              const status = statusIconProps(u.is_online, u.state_name);
              const StatusIcon = status.Icon;
              // Show the unit's custom name (same rule as the dashboard table:
              // the backend default is `Unit <id>`, which is not a real name).
              const hasCustomName = !!u.name && u.name !== `Unit ${u.unit_id}`;
              return (
                <div key={u.unit_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center" aria-label={status.label}>
                          <StatusIcon className={`h-3.5 w-3.5 ${status.color}`} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{status.label}</TooltipContent>
                    </Tooltip>
                    <Link
                      to={`/unit/${encodeURIComponent(u.unit_id)}`}
                      state={{ backTo: "/organizations", backLabel: "Organizations" }}
                      className="min-w-0 truncate text-primary hover:underline"
                    >
                      {hasCustomName ? (
                        <>
                          <span className="font-medium">{u.name}</span>
                          <span className="ml-2 font-mono text-xs opacity-60">{u.unit_id}</span>
                        </>
                      ) : (
                        <span className="font-mono">{u.unit_id}</span>
                      )}
                    </Link>
                    {isPaid && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center shrink-0" aria-label="Paid charging">
                            <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">Paid charging — customers are billed for this unit</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-destructive hover:text-destructive"
                    onClick={() => {
                      if (confirm(`Remove unit "${u.unit_id}"? Access will be revoked from all organization users.`))
                        removeUnitM.mutate(u.unit_id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
          </div>
          </TooltipProvider>
        )}
      </div>

      {/* Reports (admin only) — brief export in the slide panel, full report on the page */}
      {isAdmin && mode === "panel" && <ExportSessionsSection orgId={orgId} />}
      {isAdmin && mode === "page" && (
        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="font-semibold text-sm">Reports</h4>
          <OrgSessionsReport orgId={orgId} units={org.units} />
        </div>
      )}

      {/* Edit Modal */}
      <OrgFormModal
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Organization"
        initial={{
          name: org.name,
          contact_person: org.contact_person || "",
          contact_phone: org.contact_phone || "",
          contact_email: org.contact_email || "",
          paid_until: org.paid_until || "",
          comment: org.comment || "",
        }}
        onSubmit={(data) => editM.mutate(data)}
        isPending={editM.isPending}
      />

      {/* Add User Modal */}
      <AddUserModal
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
        orgId={orgId}
        existingUserIds={new Set(org.users.map((u) => u.id))}
        onAdded={refreshDetail}
      />

      {/* Add Unit Modal */}
      <AddUnitModal
        open={addUnitOpen}
        onOpenChange={setAddUnitOpen}
        orgId={orgId}
        existingUnitIds={new Set(org.unit_ids)}
        onAdded={refreshDetail}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export function OrganizationsPage() {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Client-side sorting for the org list (Users / Units / Paid Until, plus the
  // text columns). Default: unsorted (backend order by name).
  const [sorting, setSorting] = useState<SortingState>([]);

  const q = useQuery({
    queryKey: ["organizations"],
    queryFn: fetchOrganizations,
    refetchInterval: 20_000,
  });

  const filteredOrgs = useMemo(() => {
    const orgs = q.data?.organizations ?? [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orgs;
    return orgs.filter((o) => {
      const haystack = [
        o.name,
        o.contact_person,
        o.contact_email,
        o.contact_phone,
        o.account_custom_id,
        o.comment,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [q.data, searchTerm]);

  // Detail panel
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(() => {
    const saved = localStorage.getItem("orgsSelectedPanel");
    return saved ? Number(saved) : null;
  });

  const navigate = useNavigate();
  const handleOrgClick = useCallback((org: Organization) => {
    setSelectedOrgId(org.id);
    localStorage.setItem("orgsSelectedPanel", String(org.id));
  }, []);
  // Double-click opens the full details page (side panel is single click).
  const handleOrgOpen = useCallback(
    (org: Organization) => navigate(`/organizations/${org.id}`),
    [navigate],
  );

  const closePanel = useCallback(() => {
    setSelectedOrgId(null);
    localStorage.removeItem("orgsSelectedPanel");
  }, []);

  const createM = useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      toast.success("Organization created");
      setCreateOpen(false);
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message || "Create failed"),
  });

  const hasAccountCustomId = useMemo(
    () => (q.data?.organizations ?? []).some((o) => !!o.account_custom_id),
    [q.data]
  );

  const columns = useMemo<ColumnDef<Organization, any>[]>(
    () => {
      const cols: ColumnDef<Organization, any>[] = [
        {
          accessorKey: "name",
          header: "Name",
          cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
        },
        {
          accessorKey: "contact_person",
          header: "Contact",
          cell: ({ getValue }) => getValue() || <span className="opacity-40">—</span>,
        },
      ];
      if (hasAccountCustomId) {
        cols.push({
          accessorKey: "account_custom_id",
          header: "Account ID",
          cell: ({ getValue }) => {
            const v = getValue() as string | null | undefined;
            return v ? <span className="font-mono text-xs">{v}</span> : <span className="opacity-40">—</span>;
          },
        });
      }
      cols.push(
        {
          accessorKey: "user_count",
          header: "Users",
          cell: ({ getValue }) => <Badge variant="info" className="badge-sm">{getValue()}</Badge>,
        },
        {
          accessorKey: "unit_count",
          header: "Units",
          cell: ({ getValue }) => <Badge variant="info" className="badge-sm">{getValue()}</Badge>,
        },
        {
          // Total nameplate power capacity (sum of each charger's max current).
          // Map to undefined when absent so tanstack sorts empty rows last.
          id: "capacity_kw",
          accessorFn: (o) => o.total_capacity_kw ?? undefined,
          header: "Capacity",
          cell: ({ row }) => {
            const kw = row.original.total_capacity_kw;
            if (typeof kw !== "number") return <span className="opacity-40">—</span>;
            const amps = row.original.total_capacity_amps;
            return (
              <span className="tabular-nums" title={typeof amps === "number" ? `${amps.toLocaleString()} A` : undefined}>
                {kw.toLocaleString()} kW
              </span>
            );
          },
        },
        {
          id: "paid_until",
          // Map null → undefined so tanstack's sortUndefined can push orgs with
          // no paid-until date to the end in BOTH sort directions (null is not
          // treated as undefined by the sorter otherwise).
          accessorFn: (o) => o.paid_until ?? undefined,
          header: "Paid Until",
          sortUndefined: "last",
          cell: ({ getValue }) => paidBadge((getValue() as string | undefined) ?? null),
        },
      );
      return cols;
    },
    [hasAccountCustomId]
  );

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Organizations</h1>
          <div className="text-sm opacity-70">Manage organizations, assign users and units</div>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>+ New Organization</Button>
        )}
      </div>

      {/* Table + Detail Panel */}
      <div className="flex gap-4 items-stretch">
        <div className="flex-1 min-w-0">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
              <CardTitle>All Organizations</CardTitle>
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                <Input
                  className="pl-9"
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              {q.isError ? (
                <div className="text-sm text-error">Failed to load organizations</div>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredOrgs}
                  enableSorting
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isLoading={q.isLoading}
                  getRowId={(o) => String(o.id)}
                  onRowClick={handleOrgClick}
                  onRowDoubleClick={handleOrgOpen}
                  selectedRowId={selectedOrgId ? String(selectedOrgId) : undefined}
                  emptyMessage={searchTerm ? "No matching organizations" : "No organizations yet"}
                />
              )}
            </CardContent>
          </Card>
        </div>

        <SlidePanel open={!!selectedOrgId} onClose={closePanel} title="Organization Details" width="w-[520px]">
          {selectedOrgId && (
            <OrgDetailPanel orgId={selectedOrgId} onDeleted={closePanel} />
          )}
        </SlidePanel>
      </div>

      {/* Create Modal */}
      <OrgFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Organization"
        initial={emptyForm}
        onSubmit={(data) => createM.mutate(data)}
        isPending={createM.isPending}
      />
    </div>
  );
}
